import { Component, OnInit, Input, Output, EventEmitter, isDevMode, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { ImageFile } from '../models/imageFile';
import { WebApiService } from 'starbase-login';
import { ImageDate } from '../models/imageDate';
import { libraryViewModes } from '../libraryViewModes';
import { AlphaSortArrayByProperty, TrackByUID, TruncateString, createAnchorFromLink, isStringUrl } from 'starbase-launcher';
import { KeywordLinking, LU_Keyword } from '../models/keyword';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { LUSourceService } from '../services/luSource.service';
import { basicFade } from '../animations';
import { LUSource } from '../models/luSource';
import { map } from 'rxjs/operators';
import { RelatedImageSetViewDataModel } from '../models/relatedImageSetViewDataModel';
import { ImageService } from '../services/image.service';
import { BreakpointService, BreakpointState } from '../breakpoint.service';
import { ThumbnailType, THUMBNAIL_TEMPLATES, ThumbnailTemplateConfig } from '../models/thumbnailType';

@Component({
  selector: 'app-thumbnail',
  templateUrl: './thumbnail.component.html',
  styleUrls: ['./thumbnail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    basicFade
  ]
})
export class ThumbnailComponent implements OnInit {
  /** This should be true if the thumbnail is the "cover" for a bound set.
   * This will cause the display to look like a stack of thumbnails. */
  // @Input() public isStackedThumbnail = null;


  /** Animated vertical panning of the image when moused-over.
   *
   * @default true. */
  @Input() animate = true; // When true, pans vertically when mouse-over.
  /** The ImageFile object with which the Thumbnail renders the image path and other data. */
  @Input() imageData: ImageFile;

  @Input() relatedImageSetViewData: RelatedImageSetViewDataModel

  /**
   * Boolean for whether the thumbnail is going to show stacked images for related image sets.
   * @default false
   */
  @Input() isRelatedImageSetViewMode: boolean = false;
  @Input() isGalleryMode: boolean = false;
  /**
   *  Type for thumbnails, thumbnailType controls this, can be used for future various related set thumbnail additions.
   */
  @Input() thumbnailType: ThumbnailType = ThumbnailType.REGULAR;
  @Input() updateSeqMode = false;
  @Input() filteringByRSName: string;
  @Input() rsSequenceNumber: number;
  /** Set the display mode for this thumbnail
   *
   * @see `libraryViewModes` enum
   * @default CondensedMode (0) */
  @Input() expandMode = libraryViewModes.condensedMode; // 0 is thumb grid; 1 is large grid; 2 is list style; 3 is summary style
  /** Whether to show source info upon mouseover. Turn off for recently-viewed usage.
   *
   * @default true */
  @Input() showSourceInfo = true;

  /** Indicates whether to display in Batch Edit Mode
   * this mode displays the checkbox and applies other
   * small display tweaks. It also prevents the click emitter from firing. */
  @Input() batchEditMode = false; // Triggered when Select Images Mode is toggled on parent component
  /** Used when there is a search term applied to use when slicing the keywords array. */
  @Input() searchTerm = '';
  @Input() tooltipDelay = 750; // ms
  /** The maximum keywords to list in the preview.
   *
   * @default 8 */
  @Input() maxKeywordsToShow = 8;

  /** This emits the relevant ImageFile data when fired. */
  @Output() recordSelectedEmitter: EventEmitter<ImageFile | RelatedImageSetViewDataModel> = new EventEmitter<ImageFile | RelatedImageSetViewDataModel>();
  @Output() reloadEmitter = new EventEmitter<LU_Keyword>();

  @Output() updatedSeqNumber = new EventEmitter<any>();
  @Output() rsSeqNoUpdateActive = new EventEmitter<any>();

  @ViewChild('updateSeqInput') updateSeqInput: ElementRef;

  libraryViewModes = libraryViewModes; // import enum for template use

  /** If selected from the gallery, this value will be true and the checkmark should appear. */
  public selected = false; // Represents instance's "selected" status in Batch Edit mode

  /** Used for displaying the order in which thumbnails were chosen.
   * The template will display n + 1. */
  public sequenceNumber: number;

  public rsSelected = false;

  updatedNo = null;
  matchedRecordText: boolean = false;

  trackByUID = TrackByUID;
  truncate = TruncateString;

  imagePath: string; // file path to read from for the <img> src. This may differentiate from the imageData's thumbnail path string.

  longMonth = ImageDate.longMonth;
  unMatchingKeywords: KeywordLinking[] = [];

  hovering = false;
  isHandset = false;
  private _subscription;

  convertToAnchor = createAnchorFromLink;
  isStringUrl = isStringUrl;
  relatedSetImageSetDataName: string;
  relatedImageSetRecordCount: number;
  relatedImageSetStackTopImageToDisplay;

  relatedImageSetToPass: RelatedImageSetViewDataModel

  sourceData$: Observable<LUSource>;

  keywordsToShow: LU_Keyword[] = [];

  public templateConfig: ThumbnailTemplateConfig;
  public hasTemplate: boolean = false;

  constructor(private cd: ChangeDetectorRef, private sourceLookupSvc: LUSourceService, private imgSvc: ImageService,  private breakpointService: BreakpointService) { }

  ngOnInit() {
    console.log("searchTerm", this.searchTerm);
  this.templateConfig = THUMBNAIL_TEMPLATES[this.thumbnailType];
  this.hasTemplate = this.thumbnailType !== ThumbnailType.REGULAR;

  this._subscription = this.breakpointService.handset$.subscribe(
    isHandset => {
      this.isHandset = isHandset;
      if (this.isHandset) {
        this.expandMode = libraryViewModes.gridLargeMode;
      }
    }
  );

  this.imageData = new ImageFile().deserialize(this.isRelatedImageSetViewMode || this.thumbnailType == ThumbnailType.LEDGER_SET ? this.relatedImageSetViewData.thumbnailImage : this.imageData);

  if (isDevMode() && environment.testMode) { 
      this.imageData.fixPathForDevServers();
      this.imagePath = WebApiService.GetAPIServerAddress() + '/' + this.imageData.thumbnailPath;
    } else {
      this.imagePath = (this.isRelatedImageSetViewMode || this.thumbnailType == ThumbnailType.LEDGER_SET ? this.relatedImageSetViewData.thumbnailImage.thumbnailPath : this.imageData.thumbnailPath) + '&' + Date.now(); // cache block workaround
  }

  if (this.isRelatedImageSetViewMode || this.thumbnailType == ThumbnailType.LEDGER_SET) {
    this.relatedSetImageSetDataName;
    this.relatedImageSetRecordCount = 0;
    this.relatedImageSetStackTopImageToDisplay = 0;

    if (this.relatedImageSetViewData?.matchedRecords > 0) {
      this.matchedRecordText = true;
    }
  }

  // Handle Regular Image Mode (not related image set view)
  if (!this.isRelatedImageSetViewMode && this.thumbnailType != ThumbnailType.LEDGER_SET) {
    // Load source data for regular images
    this.sourceData$ = this.sourceServerCall();
    
    // Handle dates
    if (this.imageData.relevantDates && this.imageData.relevantDates.length) {
      this.imageData.relevantDates[0] = new ImageDate(this.imageData.relevantDates[0].month)
        .deserialize(this.imageData.relevantDates[0]);
    }

    // Handle keywords
    if (this.imageData.imageKeywords) {
      this.handleKeywordsData();
    }
  } else if (this.thumbnailType == ThumbnailType.LEDGER_SET) {
    this.sourceData$ = this.sourceServerCall();
  }

  if (this.thumbnailType === ThumbnailType.LEDGER_SET) {
    // Show match text if matchedRecords exists and is less than total
    if (this.relatedImageSetViewData?.matchedRecords !== undefined &&  this.relatedImageSetViewData?.matchedRecords !== 0) {
      this.matchedRecordText = true;
    } else {
      this.matchedRecordText = false;
    }
  }

  // Update sequence subscription
  this.imgSvc.thumbnailSelectedTrigger$.subscribe(d => {
    if (this.imageData.uid !== d && this.rsSelected) {
      if (this.updatedNo || this.updatedNo === 0) {
        this.sendUpdatedSeqNo();
      } else {
        this.cancelSeqNoUpdate();
        this.cd.markForCheck();
      }
    }
  });
  }

  public changeDetection(): void {
    this.cd.markForCheck();
  }

  setFocusOnElement() {
    setTimeout(() => {
      if (this.updateSeqInput && this.updateSeqInput.nativeElement) {
        this.updateSeqInput.nativeElement.focus();
      }
    }, 0);
  }

  getContainerBackgroundStyle(): any {
    // For thumbnails with templates, no background on container
    if (this.hasTemplate) {
      return {};
    }
    // For regular thumbnails, set background image on container
    return { 'background-image': `url(${this.imagePath})`, 'overscroll-behavior': 'auto' };
  }
  
  getThumbnailTypeClass() : string {
    return `thumbnail-type-${this.thumbnailType}`;
  }

  /** This is tied to the click event on the parent div for the template.
   * Will emit the ImageFile relevant to this instance from the recordSelectedEmitter Output.
   * If batchEditMode is true, then nothing is done. */
  selectRecord() { // opens the Single Image View for this record unless batchEditMode is on, in which case it will only emit the record ID.
    if (this.batchEditMode) {
      this.cd.detectChanges();
      return;
    }

    if (this.updateSeqMode) { // fire this when user submits the new number
      this.imgSvc.thumbnailSelected(this.imageData.uid);
      this.rsSelected = true;
      this.setFocusOnElement();
      this.cd.detectChanges();
      return
    }

    if (this.thumbnailType === ThumbnailType.LEDGER_SET || this.isRelatedImageSetViewMode) {
      this.recordSelectedEmitter.emit(this.relatedImageSetViewData);
    } else {
      this.recordSelectedEmitter.emit(this.isRelatedImageSetViewMode ? this.relatedImageSetViewData : this.imageData);
    }
  }

  cancelSeqNoUpdate(){
    this.rsSelected = false;
  }

  sendUpdatedSeqNo() { 
    if (this.updateSeqMode && this.updatedNo !=  null) { // fire this when user submits the new number
      this.updatedSeqNumber.emit([this.updatedNo, this.rsSequenceNumber]);
    }
    this.rsSelected = false;
    this.updatedNo = null;
    return
  }

  // sorting, rearranging, and highlighting of keyword data
  private handleKeywordsData(): void {
    this.imageData.imageKeywords = AlphaSortArrayByProperty(this.imageData.imageKeywords, 'lU_Keyword.keyword');
      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase();
        const matching = this.imageData.imageKeywords.filter(k => k.lU_Keyword.keyword.toLowerCase().includes(search));
        this.unMatchingKeywords = this.imageData.imageKeywords.filter(k => !k.lU_Keyword.keyword.toLowerCase().includes(search));
        this.unMatchingKeywords.unshift(...matching); // move search-matched keywords to the front of the array
        this.imageData.imageKeywords = this.unMatchingKeywords.slice(0, this.maxKeywordsToShow);
      } else {
        this.imageData.imageKeywords = this.imageData.imageKeywords.slice(0, this.maxKeywordsToShow);
      }
    this.keywordsToShow = this.procKeywordsToShow();
    this.cd.markForCheck();
  }

  private sourceServerCall(): Observable<LUSource> {
    if (this.thumbnailType == ThumbnailType.LEDGER_SET) {
      return this.sourceLookupSvc.getData(undefined, this.relatedImageSetViewData.thumbnailImage.sourceId)
        .pipe(map(s => this.imageData.source = s));
    }
    return this.sourceLookupSvc.getData(undefined, this.imageData.sourceId)
        .pipe(map(s => this.imageData.source = s));
  }

  private procKeywordsToShow(): LU_Keyword[] {
    if (!this.imageData.imageKeywords || !this.imageData.imageKeywords.length) {
 return [];
}
    // if (this.expandMode !== libraryViewModes.listMode) { return []; }
    // if (this.searchTerm) {
    //   return this.unMatchingKeywords.slice(0, this.maxKeywordsToShow);
    // } else {
    //   return this.imageData.imageKeywords.slice(0, this.maxKeywordsToShow);
    // }
    return this.imageData.imageKeywords.map(uk => uk.lU_Keyword);
  }

}
